/**
 * BPOTranscriptReviewPage
 *
 * A streamlined transcript review experience for BPO agents to extract claim
 * information (claim number + adjuster info) from successful Voice AI calls.
 *
 * The page displays the call transcript with audio playback on the right,
 * and a simplified form on the left with only essential fields.
 */
/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import WaveSurfer from 'wavesurfer.js';

import AudioWaveform from '@/components/audio-conversation/AudioWaveform';
import { useGetMatterQuery } from '@/services/api/mattersService';
import axiosInstance from '@/services/axiosConfig';

interface TranscriptSegment {
  id: string;
  text: string;
  start_time: number;
  end_time: number;
  speaker: string;
}

interface TranscriptReviewContext {
  call_id: string;
  bland_call_id?: string;
  recording_url?: string;
  audio_url?: string; // Presigned S3 URL for audio playback
  summary?: string;
  to_number?: string;
  from_number?: string;
  started_at?: string;
  call_length?: number;
  concatenated_transcript?: string;
  transcript_segments?: TranscriptSegment[];
  conversation?: {
    id: string;
    audio_path?: string;
    duration?: number;
    start_timestamp?: string;
  };
  task_id?: string;
  task?: {
    id: string;
    title?: string;
    assignee_id?: string;
    assignee_name?: string;
  };
  matter_id?: string;
  matter?: {
    id: string;
    name?: string;
  };
  workflow_id?: string;
  run_id?: string;
}

type SubmissionOutcome = 'claim_info_extracted' | 'call_not_successful';

export const BPOTranscriptReviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const callId = searchParams.get('call_id') || '';
  const taskId = searchParams.get('task_id') || '';
  const matterId = searchParams.get('matter_id') || '';
  const workflowId = searchParams.get('workflow_id') || '';
  const toNumber = searchParams.get('to_number') || '';

  const [context, setContext] = React.useState<TranscriptReviewContext | null>(
    null,
  );
  const [isLoadingContext, setIsLoadingContext] = React.useState(true);
  const [contextError, setContextError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submissionResult, setSubmissionResult] = React.useState<{
    success: boolean;
    outcome: SubmissionOutcome;
    message: string;
  } | null>(null);

  // Form state
  const [claimNumber, setClaimNumber] = React.useState('');
  const [adjusterName, setAdjusterName] = React.useState('');
  const [handles, setHandles] = React.useState<
    Array<{ type: 'PHONE' | 'EMAIL'; value: string }>
  >([{ type: 'PHONE', value: '' }]);
  const [notes, setNotes] = React.useState('');

  // "Not successful" confirmation modal
  const [showNotSuccessfulModal, setShowNotSuccessfulModal] =
    React.useState(false);

  const addHandle = () =>
    setHandles((prev) => [...prev, { type: 'PHONE', value: '' }]);
  const removeHandle = (idx: number) =>
    setHandles((prev) => prev.filter((_, i) => i !== idx));
  const updateHandle = (idx: number, field: 'type' | 'value', val: string) =>
    setHandles((prev) =>
      prev.map((h, i) =>
        i === idx
          ? {
              ...h,
              [field]: field === 'type' ? (val as 'PHONE' | 'EMAIL') : val,
            }
          : h,
      ),
    );

  // Fetch context from backend
  React.useEffect(() => {
    const fetchContext = async () => {
      if (!callId) {
        setContextError('Missing call_id parameter');
        setIsLoadingContext(false);
        return;
      }

      try {
        const params = new URLSearchParams({ call_id: callId });
        if (taskId) params.append('task_id', taskId);
        if (matterId) params.append('matter_id', matterId);
        if (workflowId) params.append('workflow_id', workflowId);

        const response = await axiosInstance.get(
          `/api/voice-ai/transcript-review?${params.toString()}`,
        );
        setContext(response.data);
        setContextError(null);
      } catch (err: any) {
        console.error('Failed to load transcript review context:', err);
        setContextError(
          err?.response?.data?.error || 'Failed to load call data',
        );
      } finally {
        setIsLoadingContext(false);
      }
    };

    fetchContext();
  }, [callId, taskId, matterId, workflowId]);

  // Get matter data for display
  const resolvedMatterId = context?.matter_id || matterId;
  const { data: matter } = useGetMatterQuery(resolvedMatterId, {
    skip: !resolvedMatterId,
  });

  // Use segments from context
  const segmentsRef = React.useRef<TranscriptSegment[]>([]);
  const segments = React.useMemo((): TranscriptSegment[] => {
    const newSegments = context?.transcript_segments || [];
    const prevIds = segmentsRef.current.map((s) => s.id).join(',');
    const newIds = newSegments.map((s) => s.id).join(',');
    if (prevIds !== newIds) {
      segmentsRef.current = newSegments;
    }
    return segmentsRef.current;
  }, [context?.transcript_segments]);

  // Get audio URL - presigned S3 URL from backend
  const audioUrl = context?.audio_url || null;

  // Audio playback state
  const waveSurferRef = React.useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [playingSegmentId, setPlayingSegmentId] = React.useState<string | null>(
    null,
  );

  // Search state
  const [searchQuery, setSearchQuery] = React.useState('');
  const [matchIds, setMatchIds] = React.useState<string[]>([]);
  const [activeMatchIndex, setActiveMatchIndex] = React.useState(0);
  const segmentRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Find segment containing claim number on initial load
  const claimSegmentId = React.useMemo(() => {
    if (!segments.length) return null;
    const claimSegment = segments.find(
      (s) =>
        s.text.toLowerCase().includes('claim number') ||
        s.text.toLowerCase().includes('claim #') ||
        /\b\d{8,}\b/.test(s.text),
    );
    return claimSegment?.id || null;
  }, [segments]);

  // Auto-scroll to claim segment on load
  const hasAutoScrolled = React.useRef(false);
  React.useEffect(() => {
    if (claimSegmentId && !hasAutoScrolled.current) {
      hasAutoScrolled.current = true;
      setTimeout(() => {
        segmentRefs.current[claimSegmentId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        setPlayingSegmentId(claimSegmentId);
      }, 500);
    }
  }, [claimSegmentId]);

  // Search match computation
  const computedMatchIds = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return segments
      .filter((seg) => seg.text.toLowerCase().includes(q))
      .map((seg) => seg.id);
  }, [searchQuery, segments]);

  React.useEffect(() => {
    const newMatchIdsStr = JSON.stringify(computedMatchIds);
    const currentMatchIdsStr = JSON.stringify(matchIds);
    if (newMatchIdsStr !== currentMatchIdsStr) {
      setMatchIds(computedMatchIds);
      setActiveMatchIndex(0);
    }
  }, [computedMatchIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to active match
  React.useEffect(() => {
    if (!matchIds.length) return;
    const activeId = matchIds[activeMatchIndex];
    if (activeId && segmentRefs.current[activeId]) {
      segmentRefs.current[activeId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [activeMatchIndex, matchIds]);

  // Track playing segment based on current time
  React.useEffect(() => {
    if (!isPlaying || !segments.length) return;
    let current: string | null = null;
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (segments[i].start_time <= currentTime) {
        current = segments[i].id;
        break;
      }
    }
    setPlayingSegmentId((prev) => (current !== prev ? current : prev));
  }, [currentTime, isPlaying, segments]);

  const handleTogglePlayPause = () => {
    const ws = waveSurferRef.current;
    if (!ws) return;
    ws.playPause();
  };

  const handleSegmentClick = (segmentId: string) => {
    const ws = waveSurferRef.current;
    if (!ws) return;
    const dur = ws.getDuration();
    const seg = segments.find((s) => s.id === segmentId);
    if (!seg || !dur) return;
    ws.seekTo(seg.start_time / dur);
    setPlayingSegmentId(segmentId);
  };

  const gotoPrevMatch = () =>
    setActiveMatchIndex((cur) => (cur <= 0 ? matchIds.length - 1 : cur - 1));
  const gotoNextMatch = () =>
    setActiveMatchIndex((cur) => (cur >= matchIds.length - 1 ? 0 : cur + 1));

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi',
    );
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-500/50 text-white rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  const submitReview = async (outcome: SubmissionOutcome) => {
    setIsSubmitting(true);
    try {
      // Build form data
      const formData = new FormData();
      formData.append('call_id', context?.call_id || callId);
      formData.append('task_id', context?.task_id || taskId);
      formData.append('matter_id', context?.matter_id || matterId);
      formData.append('workflow_id', context?.workflow_id || workflowId);
      formData.append('outcome', outcome);

      if (outcome === 'claim_info_extracted') {
        if (claimNumber) formData.append('claim_number', claimNumber);
        if (adjusterName) formData.append('adjuster_name', adjusterName);
        handles.forEach((h) => {
          if (h.value?.trim()) {
            formData.append('adjuster_handles_type[]', h.type);
            formData.append('adjuster_handles_value[]', h.value.trim());
          }
        });
      }
      if (notes) formData.append('notes', notes);

      const resp = await axiosInstance.post(
        '/api/voice-ai/transcript-review',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (resp.data.status === 'success') {
        toast.success(resp.data.message || 'Submitted successfully');
        setSubmissionResult({
          success: true,
          outcome,
          message: resp.data.message,
        });
      } else {
        throw new Error(resp.data.error || 'Submission failed');
      }
    } catch (err: any) {
      console.error(err);
      toast.error('Submission failed', { description: err?.message });
    } finally {
      setIsSubmitting(false);
      setShowNotSuccessfulModal(false);
    }
  };

  const handleSaveClaimInfo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!claimNumber.trim()) {
      toast.error('Claim number is required');
      return;
    }
    submitReview('claim_info_extracted');
  };

  const handleMarkNotSuccessful = () => {
    setShowNotSuccessfulModal(true);
  };

  const confirmNotSuccessful = () => {
    submitReview('call_not_successful');
  };

  // Loading state
  if (isLoadingContext) {
    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center">
        <div className="text-white">Loading call data...</div>
      </div>
    );
  }

  // Error state
  if (contextError) {
    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-lg mb-2">Error</div>
          <div className="text-[#9aa3b2]">{contextError}</div>
        </div>
      </div>
    );
  }

  // Success state
  if (submissionResult) {
    const isSuccess = submissionResult.outcome === 'claim_info_extracted';
    return (
      <div className="flex-1 w-full min-h-screen bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div
            className={clsx(
              'text-4xl mb-4',
              isSuccess ? 'text-green-400' : 'text-yellow-400',
            )}
          >
            {isSuccess ? '✓' : '⚠'}
          </div>
          <div className="text-white text-lg mb-2">
            {isSuccess
              ? 'Claim Information Saved'
              : 'Call Marked as Unsuccessful'}
          </div>
          <div className="text-[#9aa3b2] text-sm">
            {submissionResult.message ||
              (isSuccess
                ? 'The task assignee has been notified with the extracted claim info.'
                : 'The task assignee has been notified that the call was not successful.')}
          </div>
          <div className="text-[#9aa3b2] text-xs mt-4">
            You can close this tab.
          </div>
        </div>
      </div>
    );
  }

  const matterName = context?.matter?.name || matter?.name || 'Unknown Matter';

  return (
    <div className="flex-1 w-full h-screen overflow-hidden bg-gradient-to-b from-[#0b1020] to-[#0d1326] flex">
      {/* Left Panel - Form */}
      <div className="w-1/2 h-full overflow-y-auto border-r border-[#1f2a44] p-6">
        <div className="max-w-xl mx-auto">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-white mb-1">
              📝 Transcript Review
            </h1>
            <p className="text-sm text-[#9aa3b2]">
              Extract claim number and adjuster info from the call.
            </p>
          </div>

          {/* Context Info */}
          <div className="mb-6 rounded-lg border border-[#1f2a44] p-4 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-[#9aa3b2]">Matter</span>
                <div className="text-white font-medium">{matterName}</div>
              </div>
              <div>
                <span className="text-[#9aa3b2]">Phone</span>
                <div className="text-white">
                  {context?.to_number || toNumber || '—'}
                </div>
              </div>
              {context?.summary && (
                <div className="col-span-2">
                  <span className="text-[#9aa3b2]">AI Summary</span>
                  <div className="text-white text-xs mt-1 bg-[#0a1228] rounded p-2 border border-[#1f2a44]">
                    {context.summary}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Not Successful Button */}
          <div className="mb-6">
            <button
              type="button"
              onClick={handleMarkNotSuccessful}
              disabled={isSubmitting}
              className="w-full rounded-lg border border-red-500/50 bg-red-500/10 text-red-400 px-4 py-3 hover:bg-red-500/20 transition-colors disabled:opacity-50 text-sm font-medium"
            >
              ❌ Call Was Not Actually Successful
            </button>
            <p className="text-xs text-[#9aa3b2] mt-2 text-center">
              Use this if the AI incorrectly marked this call as successful
            </p>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#1f2a44]" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-gradient-to-b from-[#0b1020] to-[#0d1326] text-[#9aa3b2] text-xs">
                OR EXTRACT CLAIM INFO
              </span>
            </div>
          </div>

          {/* Claim Info Form */}
          <form onSubmit={handleSaveClaimInfo} className="space-y-4">
            <div>
              <label
                htmlFor="claim_number"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Claim Number <span className="text-red-400">*</span>
              </label>
              <input
                id="claim_number"
                value={claimNumber}
                onChange={(e) => setClaimNumber(e.target.value)}
                placeholder="e.g. 26123460832"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-green-500 text-lg font-mono"
              />
            </div>

            <div>
              <label
                htmlFor="adjuster_name"
                className="block text-xs text-[#9aa3b2] mb-1"
              >
                Adjuster Name
              </label>
              <input
                id="adjuster_name"
                value={adjusterName}
                onChange={(e) => setAdjusterName(e.target.value)}
                placeholder="e.g. Jonathan Guerrero"
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-xs text-[#9aa3b2] mb-1">
                Adjuster Contact Info
              </label>
              <div className="space-y-2">
                {handles.map((h, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                  >
                    <div className="col-span-4">
                      <select
                        value={h.type}
                        onChange={(e) =>
                          updateHandle(idx, 'type', e.target.value)
                        }
                        className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-2 py-2 outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      >
                        <option value="PHONE">Phone</option>
                        <option value="EMAIL">Email</option>
                      </select>
                    </div>
                    <div className="col-span-7">
                      <input
                        value={h.value}
                        onChange={(e) =>
                          updateHandle(idx, 'value', e.target.value)
                        }
                        placeholder={
                          h.type === 'PHONE'
                            ? '+1 (555) 555-5555'
                            : 'name@example.com'
                        }
                        className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => removeHandle(idx)}
                        className="w-full p-2 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                      >
                        −
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addHandle}
                  className="px-3 py-1.5 text-xs rounded border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041]"
                >
                  + Add Contact
                </button>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !claimNumber.trim()}
                className="w-full inline-flex items-center justify-center rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-3 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Saving…' : '✓ Save Claim Information'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Right Panel - Transcript */}
      <div className="w-1/2 h-full overflow-hidden flex flex-col p-6">
        <div className="flex-1 min-h-0 rounded-lg overflow-hidden border border-[#1f2a44] bg-[#0d1326] flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#1f2a44] flex items-center justify-between bg-[#0a1228]">
            <div>
              <div className="text-sm font-semibold text-white">
                Voice AI Call
              </div>
              <div className="text-xs text-[#9aa3b2]">
                {context?.call_length
                  ? `${Math.round(context.call_length)} sec`
                  : ''}
                {context?.to_number ? ` • ${context.to_number}` : ''}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-4 py-2 border-b border-[#1f2a44] bg-[#0a1228]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9aa3b2]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transcript..."
                className="w-full pl-9 pr-20 py-2 rounded-lg border border-[#1f2a44] bg-[#0d1326] text-white text-sm placeholder-[#9aa3b2] outline-none focus:ring-2 focus:ring-green-500"
              />
              {matchIds.length > 0 && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <span className="text-xs text-[#9aa3b2]">
                    {activeMatchIndex + 1}/{matchIds.length}
                  </span>
                  <button
                    type="button"
                    onClick={gotoPrevMatch}
                    className="p-1 hover:bg-[#1f2a44] rounded"
                  >
                    <ChevronUpIcon className="h-3 w-3 text-[#9aa3b2]" />
                  </button>
                  <button
                    type="button"
                    onClick={gotoNextMatch}
                    className="p-1 hover:bg-[#1f2a44] rounded"
                  >
                    <ChevronDownIcon className="h-3 w-3 text-[#9aa3b2]" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Audio Controls */}
          {audioUrl && (
            <div className="px-4 py-3 border-b border-[#1f2a44] flex items-center gap-3 bg-[#0a1228]">
              <button
                type="button"
                onClick={handleTogglePlayPause}
                className="p-2 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <PauseIcon className="h-4 w-4" />
                ) : (
                  <PlayIcon className="h-4 w-4" />
                )}
              </button>
              <div className="flex-1">
                <AudioWaveform
                  audioUrl={audioUrl}
                  onReady={(ws) => {
                    waveSurferRef.current = ws;
                    ws.on('audioprocess', (t: number) => setCurrentTime(t));
                    ws.on('seeking', (p: number) => {
                      const dur = ws.getDuration();
                      if (dur) setCurrentTime(p * dur);
                    });
                    ws.on('play', () => setIsPlaying(true));
                    ws.on('pause', () => setIsPlaying(false));
                    ws.on('finish', () => setIsPlaying(false));
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  isPlaying={isPlaying}
                />
              </div>
              <span className="text-xs text-[#9aa3b2] font-mono">
                {formatTime(currentTime)}
              </span>
            </div>
          )}

          {/* Transcript List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {segments.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-[#9aa3b2]">
                  {context?.concatenated_transcript ? (
                    <pre className="whitespace-pre-wrap text-sm text-left">
                      {context.concatenated_transcript}
                    </pre>
                  ) : (
                    <p>No transcript available for this call</p>
                  )}
                </div>
              </div>
            ) : (
              segments.map((seg) => {
                const isActive = playingSegmentId === seg.id;
                const isMatch = matchIds.includes(seg.id);
                const isActiveMatch = matchIds[activeMatchIndex] === seg.id;

                return (
                  <div
                    key={seg.id}
                    ref={(el) => {
                      segmentRefs.current[seg.id] = el;
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSegmentClick(seg.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSegmentClick(seg.id);
                      }
                    }}
                    className={clsx(
                      'p-3 rounded-lg cursor-pointer transition-all',
                      isActive
                        ? 'bg-green-500/20 border border-green-500/50'
                        : isActiveMatch
                          ? 'bg-yellow-500/20 border border-yellow-500/50'
                          : isMatch
                            ? 'bg-[#1f2a44]/50 border border-[#1f2a44]'
                            : 'bg-[#0a1228] border border-[#1f2a44] hover:border-[#3f4a64]',
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xs text-[#9aa3b2] font-mono whitespace-nowrap pt-0.5">
                        {formatTime(seg.start_time)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span
                          className={clsx(
                            'inline-block text-xs font-medium px-2 py-0.5 rounded mb-1',
                            seg.speaker === 'agent'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400',
                          )}
                        >
                          {seg.speaker === 'agent' ? 'Agent' : 'Speaker'}
                        </span>
                        <p className="text-sm text-white leading-relaxed">
                          {highlightText(seg.text, searchQuery)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Claim Number Hint */}
          {claimSegmentId && !searchQuery && (
            <div className="px-4 py-2 border-t border-[#1f2a44] bg-[#0a1228]">
              <div className="text-xs text-green-400">
                💡 Auto-scrolled to claim number mention
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Not Successful Confirmation Modal */}
      {showNotSuccessfulModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1326] border border-[#1f2a44] rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Mark Call as Unsuccessful?
              </h3>
              <button
                type="button"
                onClick={() => setShowNotSuccessfulModal(false)}
                className="p-1 hover:bg-[#1f2a44] rounded"
              >
                <XMarkIcon className="h-5 w-5 text-[#9aa3b2]" />
              </button>
            </div>
            <p className="text-sm text-[#9aa3b2] mb-4">
              This will notify the task assignee that the Voice AI call was not
              actually successful and no claim information was obtained.
            </p>
            <div className="mb-4">
              <label className="block text-xs text-[#9aa3b2] mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why was this call not successful?"
                rows={3}
                className="w-full rounded-lg border border-[#1f2a44] bg-[#0a1228] text-white px-3 py-2 outline-none focus:ring-2 focus:ring-red-500 text-sm"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowNotSuccessfulModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-[#1f2a44] text-[#cfd6e4] hover:bg-[#182041] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmNotSuccessful}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BPOTranscriptReviewPage;
